import { React, PT, fetchApi } from '@gasbuddy/react';
import BN from 'bignumber.js';
import moment from 'moment';
import JSONTree from 'react-json-tree';
import { Modal, Button, Dimmer, Loader, Grid, Table, Icon, Popup, Header, Segment, Menu } from 'semantic-ui-react';

function checkOrX(val) {
  switch (val) {
    case true:
      return <Icon color="green" name="checkmark" />;
    case false:
      return <Icon color="red" name="close" />;
    default:
      return null;
  }
}

function popupJson(v) {
  return (
    <Popup
      on="click"
      trigger={(
        <span>click for detail</span>
      )}
      content={<JSONTree data={v} shouldExpandNode={() => true} />}
    />
  );
}

function QuantityDisplay({ accountId, periods }) {
  const [selected, setSelected] = React.useState(0);
  const showPrevPageButton = selected > 0;
  const showNextPageButton = selected < periods.length - 1;
  return (
    <div>
      <Header as="h4" block attached="top">
        Usage from
        {` ${moment(periods[selected].period_start).format('MM/DD/YY')}`}
      </Header>
      <Segment attached>
        <Table basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Transaction</Table.HeaderCell>
              <Table.HeaderCell>Quantity</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {periods[selected]?.assignments?.map(tx => (
              <Table.Row key={tx.transaction_id}>
                <Table.Cell><a href={`/payments/transaction/${accountId}/${tx.transaction_id}`}>{tx.transaction_id}</a></Table.Cell>
                <Table.Cell>{tx.quantity}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.Cell>
                {`${new BN(periods[selected].quantity).toString()} used`}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {(showPrevPageButton || showNextPageButton)
                  && (
                  <Menu pagination>
                    {showPrevPageButton && (
                    <Menu.Item as="a" icon onClick={() => setSelected(selected - 1)}>
                      <Icon name="left chevron" />
                    </Menu.Item>
                    )}
                    {showNextPageButton && (
                    <Menu.Item as="a" icon onClick={() => setSelected(selected + 1)}>
                      <Icon name="right chevron" />
                    </Menu.Item>
                    )}
                  </Menu>
                  )
                }
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Segment>
    </div>
  );
}

QuantityDisplay.propTypes = {
  accountId: PT.string.isRequired,
  periods: PT.arrayOf(PT.shape({
    period_start: PT.string,
    quantity: PT.string,
    assignments: PT.arrayOf(PT.shape({ transaction_id: PT.string, quantity: PT.string })),
  })).isRequired,
};

function UsageSummary({ plans, periods }) {
  const used = periods.reduce((p, c) => p.plus(c.usage || 0), new BN(0));
  const unused = periods.reduce((p, c) => p.plus(c.breakage || 0), new BN(0));
  return (
    <Table definition>
      <Table.Body>
        <Table.Row>
          <Table.Cell>Billed</Table.Cell>
          <Table.Cell textAlign="right">{`$${plans.reduce((p, c) => p.plus(c.billed || 0), new BN(0)).toFixed(2)}`}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Collected</Table.Cell>
          <Table.Cell textAlign="right">{`$${plans.reduce((p, c) => p.plus(c.billed || 0).minus(c.uncollected || 0), new BN(0)).toFixed(2)}`}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Discount Used</Table.Cell>
          <Table.Cell textAlign="right">{`$${used.toFixed(2)}`}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Unused</Table.Cell>
          <Table.Cell textAlign="right">{`$${unused.toFixed(2)}`}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
}

UsageSummary.propTypes = {
  plans: PT.arrayOf(PT.shape({})).isRequired,
  periods: PT.arrayOf(PT.shape({
    period_start: PT.string,
    quantity: PT.string,
    assignments: PT.arrayOf(PT.shape({ transaction_id: PT.string, quantity: PT.string })),
  })).isRequired,
};

export class MembershipDiagnosticModal extends React.Component {
  state = {}

  loadAndGo = () => {
    const { accountId } = this.props;
    this.setState({ opened: true, loading: true }, async () => {
      const { body } = await fetchApi({
        url: '/payments/membership-diagnostic',
        body: { account_id: accountId },
      });
      this.setState({ loading: false, body });
    });
  }

  render() {
    const { opened, loading, body } = this.state;
    const { accountId } = this.props;
    const activePlan = body?.plans?.find(p => !p.terminated_at);
    return (
      <Modal
        size="large"
        open={opened}
        trigger={<Button floated="right" size="mini" onClick={this.loadAndGo}>Diagnose</Button>}
      >
        <Modal.Header>Membership Diagnostic</Modal.Header>
        <Modal.Content>
          <Dimmer.Dimmable dimmed={loading}>
            <Dimmer active={loading}>
              <Loader />
            </Dimmer>
            <div>
              <Grid columns={2} padded>
                <Grid.Column width={5}>
                  <Table definition>
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>Elapsed</Table.Cell>
                        <Table.Cell>{`${activePlan?.elapsed_periods} ${activePlan?.period_type}${activePlan?.elapsed_periods > 1 ? 's' : ''}`}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Active Card</Table.Cell>
                        <Table.Cell>{checkOrX(body?.status?.active_card)}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Provisioning</Table.Cell>
                        <Table.Cell>{checkOrX(body?.status?.services)}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Billing</Table.Cell>
                        <Table.Cell>{checkOrX(body?.status?.billing)}</Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                  <Modal
                    wide="very"
                    trigger={(
                      <Button>View Raw Response</Button>
                    )}
                  >
                    <Modal.Content>
                      <JSONTree data={body} shouldExpandNode={(k, d, l) => l <= 1} />
                    </Modal.Content>
                  </Modal>
                </Grid.Column>
                <Grid.Column width={7}>
                  {body?.quantity_audit?.length && <QuantityDisplay plans={body.plans} accountId={accountId} periods={body?.quantity_audit} />}
                </Grid.Column>
                <Grid.Column width={4}>
                  {body?.quantity_audit?.length && <UsageSummary plans={body.plans} accountId={accountId} periods={body?.quantity_audit} />}
                </Grid.Column>
              </Grid>
              {body?.actions?.length ? (
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Action Type</Table.HeaderCell>
                      <Table.HeaderCell>Description</Table.HeaderCell>
                      <Table.HeaderCell>Detail</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {body?.actions?.map(({ type, description, ...rest }, ix) => (
                      <Table.Row key={`${type}-${ix}`}>
                        <Table.Cell>{type}</Table.Cell>
                        <Table.Cell>{description}</Table.Cell>
                        <Table.Cell>{Object.keys(rest).length > 0 && popupJson(rest)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              ) : null}
            </div>
          </Dimmer.Dimmable>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => this.setState({ opened: false, body: null })}>
            OK
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

MembershipDiagnosticModal.propTypes = {
  accountId: PT.number,
};

MembershipDiagnosticModal.defaultProps = {
  accountId: 0,
};
